import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import './LitePanel.scss';
import HamburgerMenuIcon from '../icons/HamburgerMenu';
import { useCurrentProject } from 'providers/LiteProjectProvider';
import Button from '../Button';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import NvidiaLogo from 'static/img/nvidia-logo.png';
import DellLogo from 'static/img/dell-logo.png';
import HpeLogo from 'static/img/hpe-logo.png';
import OpenLink from '../icons/OpenLink';
import Metro2Icon from '../icons/Metro2';
import awsBadge from 'static/img/aws2.png';
import azBadge from 'static/img/az.png';
import gcpBadge from 'static/img/gcp.png';
import ociBadge from 'static/img/oci2.png';
import DoubleArrowIcon from '../icons/DoubleArrow';
import TriangleIcon from '../icons/Triangle';

const LitePanel = ({ onRampsMetros, metros = [], onMapFocusChange, marketplaceSegment }) => {
  const { project, phase, changePhase, phaseLoading } = useCurrentProject();

  const onRamps = {
    AWS: { name: 'Amazon Web Services', logo: awsBadge },
    Azure: { name: 'Azure Express Route', logo: azBadge },
    Google: { name: 'Google Cloud Partner Interconnect', logo: gcpBadge },
    Oracle: { name: 'Oracle FastConnect', logo: ociBadge },
  };
  const [isFirstSection, setIsFirstSection] = useState(true);
  const [panelIndex, setPanelIndex] = useState(0);
  const phases = project?.phases || [];
  const activePhaseId = phase?.id?.value;
  const panelMaxIndex = phases.length + 1;
  const lastPhaseIndex = Math.max(phases.length - 1, 0);
  const isIntroSection = panelIndex === 0;
  const isContactSection = panelIndex === panelMaxIndex;
  const isSingleSection = isIntroSection || isContactSection;
  const panelPhaseIndex = phases.length === 0 ? -1 : isSingleSection ? lastPhaseIndex : panelIndex - 1;
  const panelPhaseId = panelPhaseIndex >= 0 ? phases[panelPhaseIndex]?.id?.value : null;
  const phaseMetros = phase?.metros || [];
  const allMetros = Array.isArray(metros) ? metros : [];
  const getMetroCode = (metro) => metro?.code?.value || metro?.code;
  const getMetroNotes = (metro) => (metro?.notes || '').trim().toUpperCase();
  const getMarketplaceDensityRows = (metro) => {
    const buyers = metro?.marketplace?.buyers || [];

    if (!marketplaceSegment) return [];

    if (marketplaceSegment.name === 'All Participants') {
      const allCount = buyers.reduce((sum, { count }) => sum + (count || 0), 0);
      return [{ name: marketplaceSegment.name, count: allCount }];
    }

    const segmentMatch = buyers.find((buyer) => buyer.name === marketplaceSegment.name);
    if (segmentMatch) {
      return [{ name: segmentMatch.name, count: segmentMatch.count }];
    }

    const subSegmentRows = buyers
      .map((buyer) => buyer?.subSegments?.find((sub) => sub.name === marketplaceSegment.name))
      .filter((sub) => !!sub)
      .map((sub) => ({ name: sub.name, count: sub.count }));

    return subSegmentRows;
  };
  const phaseHqMetro = phaseMetros.find((metro) => getMetroNotes(metro) === 'HQ') || phaseMetros.find((metro) => getMetroCode(metro) === 'HQ');

  const getMetroIdValue = (metro) => metro?.id?.value || metro?.id;
  const phaseMetroIds = new Set(phaseMetros.map((metro) => getMetroIdValue(metro)));
  const phaseCatalogMetros = allMetros.filter((metro) => phaseMetroIds.has(getMetroIdValue(metro)));

  const hqMetro =
    phaseCatalogMetros.find((metro) => getMetroIdValue(metro) === getMetroIdValue(phaseHqMetro)) ||
    phaseCatalogMetros.find((metro) => getMetroNotes(metro) === 'HQ') ||
    phaseCatalogMetros.find((metro) => getMetroCode(metro) === 'HQ');

  const hqMetroLatencies = hqMetro
    ? hqMetro
        .getPaths(phaseCatalogMetros, allMetros)
        .tos.filter((to) => !to.metro.id.equals(hqMetro.id))
        .sort((a, b) => a.distance - b.distance)
    : [];
  useEffect(() => {
    if (isSingleSection && !isFirstSection) {
      setIsFirstSection(true);
    }
  }, [isSingleSection, isFirstSection]);

  useEffect(() => {
    if (panelIndex > panelMaxIndex) {
      setPanelIndex(panelMaxIndex);
    }
  }, [panelIndex, panelMaxIndex]);

  useEffect(() => {
    if (!panelPhaseId || panelPhaseId === activePhaseId) return;
    changePhase(panelPhaseId);
  }, [panelPhaseId, activePhaseId, changePhase]);

  const handlePhaseSelect = (phaseId) => {
    setIsFirstSection(true);
    const selectedIndex = phases.findIndex((phaseItem) => phaseItem.id.value === phaseId);
    const nextPanelIndex = selectedIndex >= 0 ? selectedIndex + 1 : 1;
    setPanelIndex(nextPanelIndex);
    if (phaseId && phaseId !== activePhaseId) {
      changePhase(phaseId);
    }
  };

  const phaseSectionOrder = [
    { key: 'Core Location', label: 'Positioning core infrastructure where it is needed the most' },
    { key: 'Interconnections', label: 'Connecting to clouds and across locations privately and redundantly' },
    { key: 'Edge Locations', label: 'Expanding your reach closer to your edge' },
    { key: 'Low Latency', label: 'Enabling real-time data processing and transfer with low latency' },
    { key: 'Potential Expansion', label: 'Driving innovation and growth with digital ecosystems' },
  ];

  const hqMetroCodes = phaseMetros
    .filter((metro) => getMetroNotes(metro) === 'HQ')
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const metroCodesWithOnRamps = phaseMetros
    .filter((metro) => {
      const metroCode = getMetroCode(metro);
      return onRampsMetros?.some((onRamp) => {
        const onRampMetroCode = onRamp?.metroCode?.value || onRamp?.metroCode;
        return onRampMetroCode === metroCode;
      });
    })
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const metroCodesWithInfrastructureLabels = phaseMetros
    .filter((metro) => !!metro?.notes)
    .map((metro) => getMetroCode(metro))
    .filter((code) => !!code);
  const edgeInfrastructureMetro = phaseMetros.find((metro) => metro?.networkEdgeEnabled || getMetroNotes(metro) === 'EDGE');
  const interconnectionSecondaryCodes = Array.from(new Set([...hqMetroCodes, ...metroCodesWithOnRamps, ...metroCodesWithInfrastructureLabels]));
  const lowLatencySecondaryCodes = Array.from(new Set(hqMetroCodes));

  // Configure map focus by section:
  // - { mode: 'all' } => fit to everything added in the current phase
  // - { mode: 'metroCode', metroCode: 'SV' } => center on one metro
  // - { mode: 'metroCodes', metroCodes: ['SV', 'DC'] } => fit to selected metros
  // - { mode: 'maxZoomOut' } => force a global zoomed-out view
  // Per section, you can also define:
  // - { true: { ...focus }, false: { ...focus } } => depends on isFirstSection
  const sectionMapFocusConfig = {
    'Introduction': { mode: 'maxZoomOut' },
    'Core Location': {
      true: hqMetroCodes.length ? { mode: 'metroCodes', metroCodes: hqMetroCodes } : { mode: 'all' },
      false: { mode: 'all' },
    },
    'Interconnections': {
      true: hqMetroCodes.length ? { mode: 'metroCodes', metroCodes: hqMetroCodes } : { mode: 'all' },
      false: interconnectionSecondaryCodes.length ? { mode: 'metroCodes', metroCodes: interconnectionSecondaryCodes } : { mode: 'all' },
    },
    'Edge Locations': {
      true: { mode: 'all' },
      false: edgeInfrastructureMetro ? { mode: 'metroCode', metroCode: getMetroCode(edgeInfrastructureMetro) } : { mode: 'all' },
    },
    'Low Latency': {
      true: { mode: 'all' },
      false: lowLatencySecondaryCodes.length ? { mode: 'metroCodes', metroCodes: lowLatencySecondaryCodes } : { mode: 'all' },
    },
    'Potential Expansion': {
      true: { mode: 'all' },
      false: { mode: 'all' },
    },
    'Contact Us': { mode: 'maxZoomOut' },
  };

  const sections = {
    'Introduction': {
      header: { true: 'Drive your future forward with Equinix' },
      content: {
        true: (
          <div>
            <p>
              This guided summary highlights the recommended locations, connectivity and ecosystem opportunities derived from your project data. Use the
              controls on the right to move through each section and see how the strategy unfolds.
            </p>
            <h4>Supporting over 10,000 organizations</h4>
            <p>
              Using our gained expertise in optimizing digital infrastructure amidst constantly evolving markets, technologies and operational models, we’ve
              created a tailored interconnected digital infrastructure design recommendation for your organization.
            </p>
            <div className="phase-list" role="list">
              {phaseSectionOrder.slice(0, phases.length).map((phaseMeta, index) => (
                <button
                  key={`intro-phase-${phases[index]?.id?.value || index}`}
                  type="button"
                  className="phase-list-item"
                  role="listitem"
                  onClick={() => handlePhaseSelect(phases[index]?.id?.value)}>
                  <span className="phase-list-number">{index + 1}.</span>
                  <span className="phase-list-text">{phaseMeta.label}</span>
                  <span className="phase-list-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              ))}
            </div>
            <p>Let’s take a closer look at how Equinix can help you.</p>
            <div className={'separator-line'} />
            <p className="small-text">
              This tailored report was auto-generated on Jan 9, 2026 based on the information provided during the Fabric design guide survey. For reference,
              your survey responses can be found here. To learn more or retake the survey, visit Fabric design guide.
            </p>
          </div>
        ),
      },
      isSingleSection: true,
    },
    'Core Location': {
      header: { true: 'Positioning core infrastructure where it is needed the most', false: 'Recommended Equinix core metro locations' },
      content: {
        true: (
          <div>
            <h4>Solving for proximity and sovereignty</h4>
            <p>
              Strategically deploying your core compute, storage and networking infrastructure close to your highest concentrations of internal and external
              users is crucial for delivering high-quality digital experiences and adhering to local data laws. You can leverage carrier-neutral colocation data
              centers to reduce latency, improve performance and simplify regulatory compliance.
            </p>
            <h4>High-performance data centers</h4>
            <p>
              Equinix operates 270+ high-performance data centers across 76 major metros in 36 countries, providing unmatched global reach. With high-density
              cooling capabilities and support for 30+ kW per cabinet, our facilities are purpose-built to handle the most demanding AI deployments.
            </p>
            <div className={'separator-line'} />
            <h4>Distributed AI</h4>
            <p>
              Equinix’s solutions for distributed AI combines the unique strengths of clouds, AI factories, and neoclouds with advanced data centers,
              strategically interconnected across the globe through a private high-speed fabric. This approach deploys:
              <ul>
                <li>workloads where they deliver maximum value</li>
                <li>training where compute is abundant</li>
                <li>sensitive processing where privacy and sovereignty is protected</li>
                <li>inference where data is generated</li>
              </ul>
              Learn more about distributed AI, the AI ecosystem at Equinix and our partnerships with the world’s leading providers.
            </p>
            <div className="brand-row">
              <img src={NvidiaLogo} alt="NVIDIA Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
            <div className="brand-row">
              <img src={DellLogo} alt="Dell Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
            <div className="brand-row">
              <img src={HpeLogo} alt="HPE Logo" />
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros?.map((metro) => (
              <div className="metro-row" key={`core-metro-${metro?.id?.value || metro?.code?.value || metro?.name}`}>
                <Metro2Icon width={21} height={21} viewBox="0 0 21 21" />
                {metro.name}
              </div>
            ))}
          </div>
        ),
      },
      explanation:
        'Deployment locations in relation to the concentration of your users and cloud resources are indicated by the large red dots. The smaller red dots represent other Equinix locations that could serve as alternatives or additions to the recommended ones.',
    },
    'Interconnections': {
      header: {
        true: 'Connecting to clouds and across locations privately and redundantly',
        false: 'Cloud on-ramps to clouds you use at Equinix core metro locations',
      },
      content: {
        true: (
          <div>
            <h4>Network Optimization</h4>
            <p>
              With Fabric, our Network as a Service (NaaS) offering, we enable you to securely connect your clouds and locations via our global network
              backbone. This ensures reliable data transfer with greater flexibility than traditional network technologies, while avoiding the security and
              performance risks inherent to the public internet. With direct cloud on-ramps hosted inside Equinix data centers, you gain private, low-latency
              access to major cloud providers, ensuring consistent performance and reduced data egress costs for your hybrid multicloud network.
            </p>
            <h4>Public clouds, private access</h4>
            <p>
              With access to more than 220 cloud on-ramps—twice as many as the nearest competitor—Equinix holds nearly 40% of the global market share for cloud
              on-ramps. This empowers you to connect to the clouds and locations you need with advanced speed, security and reliability. Learn more about our
              strategic partnerships with leading cloud service providers to deliver secure, low-latency cloud access.
            </p>
            <div className="brand-row">
              <div className="logo-name">
                <img src={azBadge} alt="Microsoft Azure Logo" />
                Microsoft Azure
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
            <div className="brand-row">
              <div className="logo-name">
                <img src={awsBadge} alt="Amazon Web Services Logo" />
                Amazon Web Services
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
            <div className="brand-row">
              <div className="logo-name">
                <img src={gcpBadge} alt="Google Cloud Logo" />
                Google Cloud
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
            <div className="brand-row">
              <div className="logo-name">
                <img src={ociBadge} alt="Oracle Cloud Logo" />
                Oracle
              </div>
              <OpenLink width={20} height={20} color="#707073" viewBox="0 0 12 12" />
            </div>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros?.map((metro) => {
              const metroCode = metro.code.value;
              const matchingOnRamps = onRampsMetros?.filter((onRamp) => onRamp.metroCode.value === metroCode);
              const cloudsByCount = matchingOnRamps?.reduce((acc, onRamp) => {
                acc[onRamp.cloud] = (acc[onRamp.cloud] || 0) + onRamp.eqxCenters;
                return acc;
              }, {});
              const cloudEntries = Object.entries(cloudsByCount);
              const metroKey = metro?.id?.value || metroCode || metro?.name;

              if (cloudEntries.length === 0) {
                return null;
              }

              return (
                <div className="metro-information" key={`metro-clouds-${metroKey}`}>
                  <div className="metro-header">
                    <Metro2Icon width={21} height={21} viewBox="0 0 21 21" />
                    {metro.name}
                  </div>
                  <div className="metro-table">
                    {cloudEntries.map(([cloud, count]) => (
                      <div className="row" key={`${metroKey}-${cloud}`}>
                        <div className="logo">
                          <img src={onRamps[cloud].logo} alt={`${onRamps[cloud].name} Logo`} /> {onRamps[cloud].name}
                        </div>
                        {count}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ),
      },
      explanation:
        'Connectivity between recommended deployments is visualized based on Equinix’s Network as a Service (NaaS) offering, with monthly average round-trip times provided to indicate expected latency on these routes.',
    },
    'Edge Locations': {
      header: { true: 'Expanding your reach closer to your edge', false: 'Recommended Equinix edge metro locations' },
      content: {
        true: (
          <div>
            <h4>Digital experiences at the edge</h4>
            <p>
              Delivering high-quality digital experiences in smaller and remote markets requires effective cloud connectivity and localized data traffic
              management, which can be challenging when you’re also trying to reduce complexity and cost. With Equinix’s NaaS offering, you can deploy virtual
              network functions (VNFs) for SD-WAN, multicloud networking and network security closer to your users, expanding reach without needing physical
              deployments. This approach makes it easier for you to address growing demand in new markets while maintaining agility and scalability.
            </p>
            <h4>Digital infrastructure for a connected world</h4>
            <p>
              Equinix’s NaaS offering spans six continents, delivering global reach and seamless connectivity. With its own cloud router and support for virtual
              network functions from more than 15 major vendors, Equinix makes it possible for you to deploy virtual network infrastructure quickly and
              efficiently through:
              <ul>
                <li>Equinix Customer Portal</li>
                <li>API</li>
                <li>infrastructure-as-code solutions</li>
                <li>e.g. Terraform, Pulumi</li>
              </ul>
              This flexibility allows you to effortlessly scale operations and meet the demands of smaller, remote and new markets.
            </p>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros
              ?.filter((metro) => metro.notes === 'Edge')
              .map((metro) => (
                <div className="metro-row" key={`edge-metro-${metro?.id?.value || metro?.code?.value || metro?.name}`}>
                  <Metro2Icon width={21} height={21} viewBox="0 0 21 21" />
                  {metro.name}
                </div>
              ))}
          </div>
        ),
      },
      explanation:
        'Virtual infrastructure deployments are recommended for markets where physical deployments may not be justified due to significant capital expenditure, contractual obligations, staffing needs and other commitments.',
    },
    'Low Latency': {
      header: { true: 'Enabling real-time data processing and transfer with low-latency', false: 'Example latency' },
      content: {
        true: (
          <div>
            <h4>No time to wait</h4>
            <p>
              Tackling latency issues that impact application performance and end-user satisfaction should be a strategic priority. In the case of modern AI
              solutions, especially generative AI (GenAI), latency can significantly hinder progress. To ensure low latency for your workloads, combine robust
              connectivity through interconnection and local networks with strategically placed infrastructure. Deploying infrastructure in proximity to clouds
              as well as internal and external users can optimize performance and enhance user experience.
            </p>
            <h4>Speed, scale and performance across the globe</h4>
            <p>
              Equinix’s network of high-performance data centers spans 76 major metropolitan areas around the globe, delivering access to most of the world’s
              internet users in less than 10ms.
            </p>
          </div>
        ),
        false: (
          <div>
            <h4>To and from clouds you use</h4>
            {phase.metros.map((metro) => {
              //TODO: UNMOCK THIS
              // if(metro.services.length === 0) return null;
              // metro.services?.map((service) =>
              return (
                <div className="latency-table" key={`latency-${metro?.id?.value || metro?.code?.value || metro?.name}`}>
                  <div className="row">
                    <div className="icon-and-text">
                      <Metro2Icon width={20} height={20} viewBox="0 0 20 20" />
                      {metro.name}
                    </div>
                    <DoubleArrowIcon width={22} height={24} viewBox="0 -7 17 24" />
                    <div className="icon-and-text">
                      <img src={onRamps['Azure'].logo} alt={`${onRamps['Azure'].name} Logo`} />
                      {onRamps['Azure'].name}
                    </div>
                    {/* <img src={onRamps[service.service.logo].logo} alt={`${onRamps[service.service.logo].name} Logo`} />
                  {onRamps[service.service.logo].name} */}
                  </div>
                  <div className="latency-value">5ms round-trip time</div>
                </div>
              );
              // )
            })}
            <h4>Between Equinix metro locations</h4>
            {hqMetroLatencies.length ? (
              hqMetroLatencies.map((to) => {
                const key = `${hqMetro?.id?.value || hqMetro?.name}-${to.metro?.id?.value || to.metro?.name}`;
                return (
                  <div className="latency-table" key={key}>
                    <div className="row">
                      <div className="icon-and-text">
                        <Metro2Icon width={20} height={20} viewBox="0 0 20 20" />
                        {hqMetro.name}
                      </div>
                      <DoubleArrowIcon width={22} height={24} viewBox="0 -7 17 24" />
                      <div className="icon-and-text">
                        <Metro2Icon width={20} height={20} viewBox="0 0 20 20" />
                        {to.metro.name}
                      </div>
                    </div>
                    <div className="latency-value">{to.distance}ms round-trip time</div>
                  </div>
                );
              })
            ) : (
              <div className="latency-table">
                <div className="latency-value">No connected metros</div>
              </div>
            )}
          </div>
        ),
      },
      explanation:
        'The green circles indicate an estimated 20ms latency radius around the recommended deployments to give you a better view of the resulting low-latency coverage.',
    },
    'Potential Expansion': {
      header: { true: 'Driving innovation and growth with digital ecosystems', false: 'Potential Equinix edge metro locations' },
      content: {
        true: (
          <div>
            <h4>Hubs for growth and innovation</h4>
            <p>
              By boosting proximity and private interconnection with current and potential business partners, you can supercharge your digital supply chain.
              Expanding your infrastructure to ecosystem hubs at Equinix unlocks new opportunities for collaboration and digital transformation.
            </p>
            <h4>The Equinix ecosystem</h4>
            <p>
              With Equinix’s ecosystem, enterprises don’t just benefit from connectivity—they gain a compounding competitive advantage. Every new connection
              strengthens the network, making Equinix the essential meeting ground where industries, platforms and partners converge to innovate, accelerate
              growth and create new business value:
              <ul>
                <li>60% of the Fortune 500 </li>
                <li>More than one-third of the Forbes Global 2000 </li>
                <li>4,900+ enterprises </li>
                <li>3000 cloud & IT service providers </li>
                <li>2,000+ network service providers </li>
                <li>~400 content & digital media companies </li>
              </ul>
              All securely, strategically and intricately linked by more than 492,000+ interconnections.
            </p>
          </div>
        ),
        false: (
          <div className="metros-container">
            {phase?.metros
              ?.filter((metro) => getMetroNotes(metro).includes('EXPANSION'))
              .map((metro) => {
                const metroCode = getMetroCode(metro);
                const phaseMetroId = getMetroIdValue(metro);
                const metroWithMarketplace =
                  phaseCatalogMetros.find((catalogMetro) => getMetroIdValue(catalogMetro) === phaseMetroId) ||
                  phaseCatalogMetros.find((catalogMetro) => getMetroCode(catalogMetro) === metroCode) ||
                  metro;
                const marketplaceDensityRows = getMarketplaceDensityRows(metroWithMarketplace);
                const matchingOnRamps = onRampsMetros?.filter((onRamp) => {
                  const onRampMetroCode = onRamp?.metroCode?.value || onRamp?.metroCode;
                  return onRampMetroCode === metroCode;
                });
                const cloudsByCount = matchingOnRamps?.reduce((acc, onRamp) => {
                  acc[onRamp.cloud] = (acc[onRamp.cloud] || 0) + onRamp.eqxCenters;
                  return acc;
                }, {});
                const cloudEntries = Object.entries(cloudsByCount || {});
                const metroKey = metro?.id?.value || metroCode || metro?.name;

                return (
                  <div className="metro-information" key={`metro-expansion-${metroKey}`}>
                    <div className="metro-header">
                      <Metro2Icon width={21} height={21} viewBox="0 0 21 21" />
                      {metro.name}
                    </div>
                    <div className="metro-table">
                      {cloudEntries.map(([cloud, count]) => (
                        <div className="row" key={`${metroKey}-${cloud}`}>
                          <div className="logo">
                            <img src={onRamps[cloud].logo} alt={`${onRamps[cloud].name} Logo`} /> {onRamps[cloud].name}
                          </div>
                          {count}
                        </div>
                      ))}
                    </div>
                    {!!marketplaceDensityRows.length && (
                      <>
                        Equinix ecosystem service density:
                        <div className="metro-table">
                          {marketplaceDensityRows.map((density) => (
                            <div className="row" key={`${metroKey}-density-${density.name}`}>
                              <div className="logo">{density.name}</div>
                              {density.count}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        ),
      },
      explanation: 'Heat maps highlight where key ecosystem participants relevant to your industry meet and interconnect at Equinix.',
    },
    'Contact Us': {
      header: { true: 'Ready to take the next step?' },
      content: {
        true: (
          <div>
            <p>
              Contact us to learn more about your tailored digital infrastructure design recommendation and how your business can overcome complexity and drive
              digital innovation with Equinix.
            </p>
            <Button text={'Learn More'} isStretch onClick={() => {}} />
          </div>
        ),
      },
      isSingleSection: true,
    },
  };

  const getSectionKeyForPhase = (phaseName, phaseIndex) => {
    if (phaseName && sections[phaseName]) {
      return phaseName;
    }
    const fallbackKey = phaseSectionOrder[phaseIndex]?.key;
    return sections[fallbackKey] ? fallbackKey : phaseSectionOrder[0].key;
  };

  const HandleBack = () => {
    if (isSingleSection) {
      const prevIndex = Math.max(panelIndex - 1, 0);
      setPanelIndex(prevIndex);
      setIsFirstSection(true);
      return;
    }
    if (isFirstSection) {
      const prevIndex = Math.max(panelIndex - 1, 0);
      setPanelIndex(prevIndex);
      if (prevIndex === 0) {
        setIsFirstSection(true);
      } else {
        setIsFirstSection(false);
      }
      return;
    }
    setIsFirstSection(true);
  };

  const HandleContinue = () => {
    if (isSingleSection) {
      const nextIndex = Math.min(panelIndex + 1, panelMaxIndex);
      setPanelIndex(nextIndex);
      setIsFirstSection(true);
      return;
    }
    if (isFirstSection) {
      setIsFirstSection(false);
      return;
    }
    const nextIndex = Math.min(panelIndex + 1, panelMaxIndex);
    setPanelIndex(nextIndex);
    setIsFirstSection(true);
  };

  const activeSectionKey = isIntroSection
    ? 'Introduction'
    : isContactSection
    ? 'Contact Us'
    : getSectionKeyForPhase(phases[panelPhaseIndex]?.name, panelPhaseIndex);
  const activeSection = sections[activeSectionKey];
  const activeHeader = activeSection?.header?.[isFirstSection] ?? activeSection?.header?.true;
  const activeContent = activeSection?.content?.[isFirstSection] ?? activeSection?.content?.true;
  const phaseNameLabel = isIntroSection || isContactSection ? activeSectionKey : phases[panelPhaseIndex]?.name;

  useEffect(() => {
    if (!onMapFocusChange) return;
    if (panelPhaseId && panelPhaseId !== activePhaseId) return;

    const sectionFocusDefinition = sectionMapFocusConfig[activeSectionKey];
    const sectionFocus =
      sectionFocusDefinition && sectionFocusDefinition.mode
        ? sectionFocusDefinition
        : sectionFocusDefinition?.[isFirstSection] || sectionFocusDefinition?.true || { mode: 'all' };
    const normalizedFocus =
      sectionFocus?.mode === 'metroCodes' && (!sectionFocus.metroCodes || sectionFocus.metroCodes.length === 0) ? { mode: 'all' } : sectionFocus;

    onMapFocusChange({
      ...normalizedFocus,
      sourceKey: `${activeSectionKey}-${panelIndex}-${isFirstSection}-${activePhaseId || 'none'}`,
    });
  }, [activeSectionKey, panelIndex, isFirstSection, panelPhaseId, activePhaseId, onMapFocusChange]);

  return (
    <div className={classNames('lite-panel', { 'is-loading': phaseLoading })}>
      {phaseLoading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="loading-spinner" />
          <div className="loading-text">Loading phase...</div>
        </div>
      )}
      <div className="phase-information">
        <div className="phase-name">
          <Menu
            menuClassName={'default-menu'}
            align={'start'}
            menuButton={
              <MenuButton className="phase-name-button">
                <HamburgerMenuIcon /> {phaseNameLabel}
                <div className="triangle-container" data-testid={'triangle-icon'}>
                  <TriangleIcon width={6} height={5} color="black" />
                </div>
              </MenuButton>
            }
            transition
            portal>
            <MenuItem onClick={() => setPanelIndex(0)} disabled={isIntroSection}>
              Introduction
            </MenuItem>
            {phases.map((phaseItem) => (
              <MenuItem
                key={phaseItem.id.value}
                onClick={() => handlePhaseSelect(phaseItem.id.value)}
                disabled={!isSingleSection && phaseItem.id.value === activePhaseId}>
                {phaseItem.name}
              </MenuItem>
            ))}
            <MenuItem onClick={() => setPanelIndex(panelMaxIndex)} disabled={isContactSection}>
              Contact Us
            </MenuItem>
          </Menu>
        </div>
        <div className="progress-bar" role="list" aria-label="Phase progress">
          {phases.map((phaseItem, index) => {
            let status = 'upcoming';
            if (isContactSection) {
              status = 'complete';
            } else if (!isIntroSection) {
              status = index === panelPhaseIndex ? 'current' : index < panelPhaseIndex ? 'complete' : 'upcoming';
            }
            return (
              <span
                key={phaseItem.id.value}
                className={classNames('progress-dot', `progress-dot--${status}`)}
                role="listitem"
                aria-current={status === 'current' ? 'step' : undefined}
              />
            );
          })}
        </div>
      </div>
      <div className="header" key={`header-${panelIndex}-${isFirstSection}`}>
        {activeHeader}
      </div>
      <div className="content-container" key={`content-${panelIndex}-${isFirstSection}`}>
        {isFirstSection && !!activeSection?.explanation && <div className="explanation-box">{activeSection?.explanation}</div>}
        <div className="content">{activeContent}</div>
      </div>
      <div className="buttons-container">
        <Button text={'Back'} color="gray" isInverse onClick={HandleBack} isDisabled={panelIndex === 0} />
        <Button text={'Continue →'} onClick={HandleContinue} isDisabled={panelIndex >= panelMaxIndex} />
      </div>
    </div>
  );
};

export default LitePanel;
